import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, apiError, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { AnnualReturnsPanel } from "@/pages/AnnualReturns";
import { LedgersPanel } from "@/pages/Ledgers";
import { EwayPanel } from "@/pages/EwayBills";
import { HsnReportPanel } from "@/pages/HsnReport";
import { ItcChecksPanel } from "@/pages/ItcChecks";
import { PortalDirectCard } from "@/components/PortalDirect";
import { ClientOtpLink } from "@/components/ClientOtpLink";
import { AvailedItcPanel } from "@/pages/AvailedItc";
import { FileSpreadsheet, Upload, RefreshCw, Send, CheckCircle2, XCircle, ShieldAlert, Building2, KeyRound, Download, PlugZap, BarChart3 } from "lucide-react";

const PERIOD_OPTIONS = Array.from({ length: 18 }, (_, i) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - i);
  return `${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}`;
});
const DEFAULT_PERIOD = PERIOD_OPTIONS[1];
const periodLabel = (p) => new Date(Number(p.slice(2)), Number(p.slice(0, 2)) - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
const REVERSAL_TYPES = ["Reversals - Rule 37B [Return not filed by supplier] - Reclaimable", "Other Reversals [180] - Rule 37", "Rule 42/43 Reversals", "Other Reversals [17(5)]", "Any Other Reversal"];
const REPORTING_PERIODS = ["Current Month", "Accepted from Pending"];

/* ------------ Returns workflow ------------ */
function ReturnCard({ clientId, returnType, role, period, onChange }) {
  const [ret, setRet] = useState(null);
  const [busy, setBusy] = useState(false);
  const isChecker = role === "admin" || role === "partner";

  const load = useCallback(() => {
    return api.get(`/clients/${clientId}/returns/${returnType}?period=${period}`)
      .then((r) => setRet(r.data)).catch(() => setRet(false));
  }, [clientId, returnType, period]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await load(); toast.success(msg); onChange && onChange(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const prepare = () => act(() => api.post(`/clients/${clientId}/returns/${returnType}/prepare`, { period: period }), "Return prepared");
  const save = () => act(() => api.post(`/clients/${clientId}/returns/${returnType}/save`, { period: period }), "Saved to portal");
  const submit = () => act(() => api.post(`/clients/${clientId}/returns/${returnType}/submit`, { period: period }), "Submitted for approval");
  const approve = () => act(() => api.post(`/clients/${clientId}/returns/${returnType}/approve?period=${period}`, { note: "" }), "Approved");
  const reject = () => act(() => api.post(`/clients/${clientId}/returns/${returnType}/reject?period=${period}`, { note: "" }), "Sent back to preparer");
  const file = () => act(async () => {
    const { data } = await api.post(`/clients/${clientId}/returns/${returnType}/file?period=${period}`, { confirm: true });
    toast.success(`Filed · ARN ${data.arn}`);
  }, "Return filed");

  const s = ret?.summary;
  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4" data-testid={`return-card-${returnType}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-head text-lg font-semibold">{returnType === "GSTR1" ? "GSTR-1" : "GSTR-3B"}</div>
          <div className="small-label">Period {period} · due {ret?.due_date || "—"}</div>
        </div>
        <StatusBadge status={ret ? ret.status : "draft"} testid={`return-status-${returnType}`} />
      </div>

      {ret && s && (
        <div className="grid grid-cols-2 gap-3 font-mono text-xs">
          <div className="rounded-md bg-secondary/40 p-3"><div className="small-label">Output Tax</div><div className="text-base mt-1">{inr(s.output_tax)}</div></div>
          <div className="rounded-md bg-secondary/40 p-3"><div className="small-label">Input Credit</div><div className="text-base mt-1">{inr(s.input_tax_credit)}</div></div>
          <div className="rounded-md bg-secondary/40 p-3"><div className="small-label">Net Payable</div><div className="text-base mt-1 text-amber-300">{inr(s.net_payable)}</div></div>
          <div className="rounded-md bg-secondary/40 p-3"><div className="small-label">Invoices</div><div className="text-base mt-1">{s.sales.count + s.purchases.count}</div></div>
        </div>
      )}

      {returnType === "GSTR3B" && ret?.itc_table && (
        <div className="rounded-md border border-border bg-secondary/20 p-3 space-y-2" data-testid="gstr3b-itc-table">
          <div className="small-label">GSTR-3B Table 4 · Eligible ITC</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px]">
            {Object.entries(ret.itc_table.available).filter(([, v]) => v.total > 0).map(([code, v]) => (
              <div key={code} className="flex justify-between"><span className="text-muted-foreground">{code}</span><span>{inr(v.total)}</span></div>
            ))}
            {Object.entries(ret.itc_table.reversed).filter(([, v]) => v.total > 0).map(([code, v]) => (
              <div key={code} className="flex justify-between"><span className="text-rose-300">{code} rev</span><span className="text-rose-300">-{inr(v.total)}</span></div>
            ))}
            {Object.entries(ret.itc_table.reclaimed || {}).filter(([, v]) => v.total > 0).map(([code, v]) => (
              <div key={code} data-testid="itc-reclaimed-row" className="flex justify-between"><span className="text-sky-300">{code} reclaimed</span><span className="text-sky-300">{inr(v.total)}</span></div>
            ))}
          </div>
          <div className="flex justify-between border-t border-border pt-2 font-mono text-xs"><span className="text-muted-foreground">Net ITC</span><span className="text-emerald-300">{inr(ret.itc_table.net_itc)}</span></div>
        </div>
      )}

      {ret?.validation?.invalid_count > 0 && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-300">
          <ShieldAlert className="inline h-3.5 w-3.5 mr-1" /> {ret.validation.invalid_count} invoice(s) failed validation — fix before filing.
        </div>
      )}

      {ret?.arn && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 font-mono text-xs text-emerald-300">ARN: {ret.arn}</div>
      )}

      {role !== "reader" && <div className="flex flex-wrap gap-2">
        {(!ret || ["draft", "prepared", "saved_to_portal"].includes(ret.status)) && (
          <button data-testid={`${returnType.toLowerCase()}-prepare-button`} onClick={prepare} disabled={busy}
            className="rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white px-3 py-2 text-xs font-medium">Prepare / Recompute</button>
        )}
        {ret && ["prepared", "saved_to_portal"].includes(ret.status) && (
          <>
            <button data-testid={`${returnType.toLowerCase()}-save-button`} onClick={save} disabled={busy}
              className="rounded-md border border-border hover:border-sky-500/40 text-sky-300 px-3 py-2 text-xs font-medium">Save to Portal</button>
            <button data-testid={`${returnType.toLowerCase()}-submit-button`} onClick={submit} disabled={busy || ret.validation?.invalid_count > 0}
              className="flex items-center gap-1 rounded-md bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white px-3 py-2 text-xs font-medium"><Send className="h-3.5 w-3.5" /> Submit for Approval</button>
          </>
        )}
        {ret && ret.status === "pending_approval" && isChecker && (
          <>
            <button data-testid={`${returnType.toLowerCase()}-approve-button`} onClick={approve} disabled={busy}
              className="flex items-center gap-1 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-2 text-xs font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>
            <button data-testid={`${returnType.toLowerCase()}-reject-button`} onClick={reject} disabled={busy}
              className="flex items-center gap-1 rounded-md border border-rose-500/40 text-rose-300 px-3 py-2 text-xs font-medium"><XCircle className="h-3.5 w-3.5" /> Reject</button>
          </>
        )}
        {ret && ret.status === "pending_approval" && !isChecker && (
          <span className="text-xs text-muted-foreground font-mono py-2">Awaiting partner approval</span>
        )}
        {ret && ret.status === "approved" && isChecker && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid={`${returnType.toLowerCase()}-file-button`} disabled={busy}
                className="flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 text-xs font-medium">File on GSTN</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-head flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-400" /> Confirm irreversible filing</AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  You are about to file <b>{returnType}</b> for <b>{period}</b> on the GST portal. This is near-irreversible.
                  <span className="block mt-2 font-mono text-xs">Output tax {inr(s?.output_tax)} · Net payable {inr(s?.net_payable)}</span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="file-cancel-button">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="file-confirm-button" onClick={file} className="bg-emerald-600 hover:bg-emerald-500">Confirm &amp; File</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {ret && ret.status === "filed" && <span className="text-xs text-emerald-400 font-mono py-2">Filed ✓</span>}
      </div>}
      {ret && (ret.prepared_by_name || ret.approved_by_name) && (
        <div className="text-[11px] text-muted-foreground font-mono border-t border-border pt-2">
          {ret.prepared_by_name && <>Prepared by: {ret.prepared_by_name}</>}
          {ret.approved_by_name && <> · Approved by: {ret.approved_by_name}</>}
        </div>
      )}
    </div>
  );
}

/* ------------ Import ------------ */
const SUPPLY_TYPES = ["REGULAR", "SEWP", "SEWOP", "SEZLUT", "IMPG", "IMPS", "RCM-RP", "RCM-URP", "ISD", "EXEMPT", "NIL RATED", "NON GST", "REVERSALS", "NO SUPPLY", "RE-CREDIT/RECLAIMED", "REVERSALS-DRC-03", "OTHERS"];

function ImportPanel({ clientId, readOnly, period }) {
  const [kind, setKind] = useState("sales");
  const [rows, setRows] = useState([]);
  const emptyRow = { invoice_no: "", invoice_date: `10-${period.slice(0, 2)}-${period.slice(2)}`, counterparty_gstin: "", counterparty_name: "", hsn: "", description: "", rate: 18, taxable_value: 0, igst: 0, cgst: 0, sgst: 0, supply_type: "REGULAR", itc_available: "", accept_pending: "", reporting_period: "", reversal_type: "" };
  const [form, setForm] = useState(emptyRow);

  const reqSeq = useRef(0);
  const load = useCallback(() => {
    const seq = ++reqSeq.current;
    api.get(`/clients/${clientId}/invoices?period=${period}&kind=${kind}`)
      .then((r) => { if (seq === reqSeq.current) setRows(r.data); }).catch(() => {});
  }, [clientId, kind, period]);
  useEffect(() => { load(); }, [load]);

  const addManual = async () => {
    try {
      await api.post(`/clients/${clientId}/import/manual`, { period: period, kind, rows: [form] });
      toast.success("Invoice added"); setForm(emptyRow); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/clients/${clientId}/import/file?period=${period}&kind=${kind}`, fd);
      toast.success(`Imported ${data.inserted} rows (${data.invalid} invalid)`); load();
    } catch (e) { toast.error(apiError(e)); }
    e.target.value = "";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {["sales", "purchase"].map((k) => (
          <button key={k} data-testid={`import-kind-${k}`} onClick={() => setKind(k)}
            className={`rounded-md px-3 py-1.5 text-xs font-mono uppercase tracking-wide border ${kind === k ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" : "border-border text-muted-foreground"}`}>{k}</button>
        ))}
        {!readOnly && <label data-testid="import-file-label" className="ml-auto flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs cursor-pointer hover:border-indigo-500/40">
          <Upload className="h-3.5 w-3.5" /> Excel / CSV
          <input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={upload} />
        </label>}
      </div>

      {!readOnly && kind === "purchase" && (
        <div className="rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="purchase-class-form">
          <div>
            <label className="small-label">Supply Type</label>
            <select data-testid="manual-supply_type-select" value={form.supply_type} onChange={(e) => setForm({ ...form, supply_type: e.target.value })}
              className="mt-1 w-full rounded-md bg-secondary/50 border border-border px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500">
              {SUPPLY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="small-label">ITC Available [Y/N]</label>
            <select data-testid="manual-itc_available-select" value={form.itc_available} onChange={(e) => setForm({ ...form, itc_available: e.target.value })}
              className="mt-1 w-full rounded-md bg-secondary/50 border border-border px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500">
              <option value="">—</option><option value="Y">Y</option><option value="N">N</option>
            </select>
          </div>
          <div>
            <label className="small-label">Accept / Pending</label>
            <select data-testid="manual-accept_pending-select" value={form.accept_pending} onChange={(e) => setForm({ ...form, accept_pending: e.target.value })}
              className="mt-1 w-full rounded-md bg-secondary/50 border border-border px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500">
              <option value="">—</option><option value="ACCEPT">ACCEPT</option><option value="PENDING">PENDING</option><option value="REJECT">REJECT</option>
            </select>
          </div>
          <div>
            <label className="small-label">Reporting Period Type</label>
            <select data-testid="manual-reporting_period-select" value={form.reporting_period} onChange={(e) => setForm({ ...form, reporting_period: e.target.value })}
              className="mt-1 w-full rounded-md bg-secondary/50 border border-border px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500">
              <option value="">—</option>{REPORTING_PERIODS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="small-label">Reversal Type</label>
            <select data-testid="manual-reversal_type-select" value={form.reversal_type} onChange={(e) => setForm({ ...form, reversal_type: e.target.value })}
              className="mt-1 w-full rounded-md bg-secondary/50 border border-border px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500">
              <option value="">— None —</option>{REVERSAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      )}

      {!readOnly && <div className="rounded-lg border border-border bg-card p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="manual-entry-form">
        {[["invoice_no", "Invoice No"], ["counterparty_gstin", "Counterparty GSTIN"], ["counterparty_name", "Name"], ["hsn", "HSN"], ["description", "Description"], ["rate", "Rate %"], ["taxable_value", "Taxable ₹"], ["igst", "IGST"], ["cgst", "CGST"], ["sgst", "SGST"]].map(([k, label]) => (
          <div key={k}>
            <label className="small-label">{label}</label>
            <input data-testid={`manual-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="mt-1 w-full rounded-md bg-secondary/50 border border-border px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
        ))}
        <div className="flex items-end">
          <button data-testid="manual-add-button" onClick={addManual} className="w-full rounded-md bg-indigo-600 hover:bg-indigo-500 text-white py-1.5 text-xs font-medium">Add Row</button>
        </div>
      </div>}

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="text-muted-foreground border-b border-border">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left">
              <th>Invoice</th><th>Counterparty</th><th>HSN</th><th>Taxable</th><th>Tax</th><th>ITC (3B)</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`invoice-row-${r.id}`} className="border-b border-border/50 [&>td]:px-3 [&>td]:py-2">
                <td>{r.invoice_no}</td>
                <td className="max-w-[220px] truncate">{r.counterparty_gstin} {r.counterparty_name && `· ${r.counterparty_name}`}</td>
                <td>{r.hsn}</td>
                <td>{inr(r.taxable_value)}</td>
                <td>{inr(r.igst + r.cgst + r.sgst)}</td>
                <td className="max-w-[220px] truncate">{r.itc_class ? <span title={`avail:${r.itc_class.itc_avail || '-'} 4B1:${r.itc_class.itc_4b1 || '-'} 4B2:${r.itc_class.itc_4b2 || '-'}`}>{r.supply_type || "—"}{[r.itc_class.itc_avail, r.itc_class.itc_4b1, r.itc_class.itc_4b2, r.itc_class.itc_4d].filter((c) => c && c !== "NA").map((c) => ` · ${c}`).join("")}</span> : "—"}{r.blocked_hint && <span data-testid={`blocked-flag-${r.invoice_no}`} title={`${r.blocked_hint.label}: ${r.blocked_hint.reason}`} className="ml-1 text-rose-400">⚠ {r.blocked_hint.clause}</span>}</td>
                <td>{r.valid ? <span className="text-emerald-400">valid</span> : <span className="text-rose-400" title={r.errors.join("; ")}>invalid</span>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No {kind} invoices for {period}.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------ Reconciliation ------------ */
const RECON_TABS = [
  { key: "matched", label: "Matched" },
  { key: "mismatch", label: "Mismatch" },
  { key: "missing_in_books", label: "Missing in Books" },
  { key: "missing_in_2b", label: "Missing in 2B" },
];

function ReconcilePanel({ clientId, readOnly, period }) {
  const [recon, setRecon] = useState(null);
  const [active, setActive] = useState("matched");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/gstr2b/fetch`, { period: period });
      const { data } = await api.post(`/clients/${clientId}/gstr2b/reconcile?period=${period}`);
      setRecon(data); toast.success("GSTR-2B reconciled");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const uploadReconcile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/clients/${clientId}/gstr2b/import/file?period=${period}`, fd);
      const { data: rec } = await api.post(`/clients/${clientId}/gstr2b/reconcile?period=${period}`);
      setRecon(rec);
      toast.success(`Imported ${data.imported} GSTR-2B rows & reconciled`);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
    e.target.value = "";
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-lg">Fetch GSTR-2B from the GST portal or import the official GSTR-2B Excel (30-sheet format), then reconcile against booked purchases. ITC is never auto-accepted.</p>
        {!readOnly && <div className="flex items-center gap-2">
          <label data-testid="reconcile-upload-label" className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:border-indigo-500/40">
            <Upload className="h-4 w-4" /> Import 2B Excel
            <input data-testid="reconcile-upload-input" type="file" accept=".xlsx,.xls" className="hidden" onChange={uploadReconcile} disabled={busy} />
          </label>
          <button data-testid="reconcile-run-button" onClick={run} disabled={busy}
            className="flex items-center gap-2 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-medium">
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Fetch &amp; Reconcile
          </button>
        </div>}
      </div>

      {recon && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {RECON_TABS.map((t) => (
              <button key={t.key} data-testid={`reconcile-filter-${t.key}`} onClick={() => setActive(t.key)}
                className={`rounded-lg border p-4 text-left ${active === t.key ? "border-indigo-500/50 bg-indigo-500/5" : "border-border bg-card"}`}>
                <StatusBadge status={t.key} />
                <div className="font-head text-2xl font-bold font-mono mt-2">{recon.counts[t.key]}</div>
              </button>
            ))}
          </div>

          <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid="reconcile-table">
            <table className="w-full text-xs font-mono">
              <thead className="text-muted-foreground border-b border-border">
                <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left">
                  <th>Invoice</th><th>Counterparty</th><th>Book Tax</th><th>Portal Tax</th><th>Delta</th>
                </tr>
              </thead>
              <tbody>
                {recon[active].map((r, i) => (
                  <tr key={`${active}-${r.invoice_no}-${r.counterparty_gstin}`} data-testid={`reconcile-row-${active}-${i}`} className="border-b border-border/50 [&>td]:px-3 [&>td]:py-2">
                    <td>{r.invoice_no}</td>
                    <td className="max-w-[220px] truncate">{r.counterparty_gstin} {r.counterparty_name && `· ${r.counterparty_name}`}</td>
                    <td>{r.book_tax != null ? inr(r.book_tax) : "—"}</td>
                    <td>{r.portal_tax != null ? inr(r.portal_tax) : "—"}</td>
                    <td className={r.delta ? "text-rose-400" : "text-muted-foreground"}>{r.delta != null ? inr(r.delta) : "—"}</td>
                  </tr>
                ))}
                {recon[active].length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Nothing in this bucket.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!recon && <div className="text-muted-foreground font-mono text-sm">Run reconciliation to see the 2B-vs-books matrix.</div>}
    </div>
  );
}

/* ------------ GST Portal: login (OTP) + download ------------ */
const REG_FIELDS = [["legal_name", "Legal name"], ["trade_name", "Trade name"], ["state_name", "State"], ["pan", "PAN"],
  ["constitution", "Constitution"], ["status", "Status"], ["registration_date", "Registered on"], ["taxpayer_type", "Taxpayer type"], ["address", "Address"]];

function RegistrationFromGstn({ reg }) {
  if (!reg?.fetched_at) { return null; }
  return (
    <div className="mt-4 rounded-md border border-border p-3 text-xs space-y-1" data-testid="portal-registration-details">
      <div className="small-label mb-1">Registration details from GSTN · {new Date(reg.fetched_at).toLocaleDateString("en-GB").replaceAll("/", "-")}</div>
      {REG_FIELDS.filter(([k]) => reg[k]).map(([k, l]) => <div key={k} className="flex gap-2"><span className="w-28 shrink-0 text-muted-foreground">{l}</span><span className="font-mono">{reg[k]}</span></div>)}
      {reg.gateway_mode === "mock" && <p className="text-amber-600 pt-1">Test mode: only state, PAN and constitution can be read from the GSTIN; the rest fills in once the GST API account is connected.</p>}
    </div>
  );
}

function GatewayCheck() {
  const [r, setR] = useState(null);
  const run = async () => {
    setR({ message: "Checking…" });
    try { const { data } = await api.get("/admin/gst-gateway/check"); setR(data); } catch (e) { setR({ ok: false, message: apiError(e) }); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
      <button data-testid="gateway-check-button" onClick={run} className="rounded-md border border-border px-2.5 py-1 hover:bg-muted">Check GST gateway connection</button>
      {r && <span data-testid="gateway-check-result" className={r.ok ? "text-emerald-600" : "text-rose-600"}>{r.message}{r.server_ip ? ` (server IP ${r.server_ip})` : ""}</span>}
    </div>
  );
}

function PortalPanel({ clientId, readOnly, period, gstin, role }) {
  const [status, setStatus] = useState(null);
  const [username, setUsername] = useState("");
  const [otpRef, setOtpRef] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(() => {
    return api.get(`/clients/${clientId}/portal/status`).then((r) => { setStatus(r.data); setUsername((u) => u || r.data.username || ""); }).catch(() => {});
  }, [clientId]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const requestOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/portal/otp`, { username });
      setOtpRef(data.otp_ref);
      if (data.gateway_mode === "mock" || !data.gateway_mode) {
        toast.info("Test mode: no OTP is sent yet. Enter any 6 digits (e.g. 123456). Once the GST API account is connected, GSTN sends the OTP to the mobile and email registered for this GSTIN.", { id: "otp-info", duration: 9000 });
      } else {
        toast.success(`OTP sent by GSTN to the registered mobile and email ${data.sent_to}`);
      }
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/portal/verify`, { otp, otp_ref: otpRef, days: 30 });
      toast.dismiss("otp-info");
      toast.success(live ? "Connected to GST for 30 days" : "Connected (test mode — no real GST connection yet)");
      setOtp(""); setOtpRef("");
      await loadStatus();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const download = async (doc) => {
    try {
      const { data } = await api.get(`/clients/${clientId}/portal/download?doc_type=${doc}&period=${period}`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${doc}-${period}.json`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${doc} from portal`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const connected = status?.connected;
  const live = status?.gateway_mode && status.gateway_mode !== "mock";
  const disconnect = async () => {
    try { await api.post(`/clients/${clientId}/portal/disconnect`); toast.success("Disconnected"); await loadStatus(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <PortalDirectCard gstin={gstin} clientId={clientId} role={role} />
      <div className="rounded-lg border border-border bg-card p-5" data-testid="portal-connection-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PlugZap className={`h-5 w-5 ${connected ? "text-emerald-400" : "text-muted-foreground"}`} />
            <span className="font-head font-semibold">GST sign-in (User ID + OTP){live ? " · live via FYN" : " · test mode"}</span>
          </div>
          <span data-testid="portal-status-badge" className={`text-xs font-mono px-2 py-0.5 rounded-md border ${connected ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-slate-500/10 text-slate-400 border-slate-500/30"}`}>
            {connected ? "Connected" : "Not connected"}
          </span>
        </div>
        <p data-testid="portal-api-note" className={`text-xs mt-2 ${live ? "text-muted-foreground" : "text-amber-600"}`}>Enter the client's GST User ID and click Send OTP: GSTN sends the OTP to the mobile and email registered for this GSTIN, and registration details are fetched after sign-in.{live ? "" : " Test mode — no data goes to or comes from GST."} One OTP keeps the session active for 30 days, provided the client has enabled API access for 30 days on the GST portal (My Profile → Manage API Access).</p>
        {status?.refresh_error && <p className="text-xs text-rose-600 mt-1" data-testid="portal-refresh-error">Last session refresh failed: {status.refresh_error}</p>}
        {(role === "admin" || role === "partner") && <GatewayCheck />}
        {connected ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <p className="text-xs text-muted-foreground font-mono" data-testid="portal-session-valid-until">Session valid until {new Date(status.valid_until).toLocaleString("en-IN")} — one OTP lasts 30 days</p>
            {new Date(status.valid_until) - Date.now() < 3 * 864e5 && <span className="text-xs text-amber-600" data-testid="portal-expiry-soon">Ends within 3 days — {status.expiry_reminder_result || "a renewal link will be emailed to the client in the next daily run"}</span>}
            {!readOnly && <button data-testid="portal-disconnect-button" onClick={disconnect} className="rounded-md border border-rose-500/40 text-rose-600 hover:bg-rose-500/10 px-3 py-1.5 text-xs font-medium">Disconnect</button>}
          </div>
        ) : readOnly ? (
          <p data-testid="portal-readonly-note" className="text-xs text-muted-foreground font-mono mt-3">Read-only access — an Admin, Partner or Staff user must connect the portal session.</p>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="small-label">GST User ID</label>
                <input data-testid="portal-username-input" value={username} onChange={(e) => setUsername(e.target.value)}
                  className="mt-1 w-full rounded-md bg-secondary/50 border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="Client's GST portal User ID" />
              </div>
              <div className="flex items-end">
                <button data-testid="portal-request-otp-button" onClick={requestOtp} disabled={busy || !username.trim()}
                  className="w-full flex items-center justify-center gap-2 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white py-2 text-sm font-medium">
                  <KeyRound className="h-4 w-4" /> Send OTP to registered mobile &amp; email
                </button>
              </div>
            </div>
            {otpRef && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="small-label">{live ? "Enter the OTP received on the registered mobile / email" : "Enter OTP (test mode: any 6 digits)"}</label>
                  <input data-testid="portal-otp-input" value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6}
                    className="mt-1 w-full rounded-md bg-secondary/50 border border-border px-3 py-2 text-sm font-mono tracking-[0.3em] focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="______" />
                </div>
                <div className="flex items-end">
                  <button data-testid="portal-verify-button" onClick={verify} disabled={busy}
                    className="w-full rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white py-2 text-sm font-medium">
                    Verify &amp; Connect
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        <RegistrationFromGstn reg={status?.registration} />
        <details className="mt-4" data-testid="client-otp-link-toggle">
          <summary className="cursor-pointer text-xs text-indigo-600">Client not with you? Email them a link to type the OTP</summary>
          <div className="mt-2"><ClientOtpLink clientId={clientId} readOnly={readOnly} onConnected={loadStatus} /></div>
        </details>
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <div className="font-head font-semibold mb-1">Download via GST API · {period} · test mode</div>
        <p className="text-xs text-muted-foreground mb-4">Available after GSTN API access is approved. Sample data only for now.</p>
        <div className="grid grid-cols-3 gap-3">
          {["GSTR1", "GSTR3B", "GSTR2B"].map((doc) => (
            <button key={doc} data-testid={`portal-download-${doc}`} onClick={() => download(doc)} disabled={!connected}
              className="flex flex-col items-center gap-2 rounded-md border border-border py-4 text-sm font-medium hover:border-indigo-500/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <Download className="h-5 w-5 text-indigo-400" />{doc.replace("GSTR", "GSTR-")}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------ Tax Liability & ITC Comparison Report ------------ */
const FY_OPTIONS = ["2026-27", "2025-26", "2024-25"];

function num(v) {
  const n = Number(v || 0);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function ComparisonReport({ clientId }) {
  const [fy, setFy] = useState("2025-26");
  const [report, setReport] = useState(null);
  const [view, setView] = useState("liability"); // liability | comparison
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    api.get(`/clients/${clientId}/reports/tax-liability-itc?fy=${fy}`)
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(false));
  }, [clientId, fy]);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/reports/tax-liability-itc/export?fy=${fy}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fy}_${report?.gstin || "client"}_Tax liability and ITC comparison.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  const tl = report?.tax_liability_summary;
  const cmp = report?.comparison_summary;

  return (
    <div className="space-y-4" data-testid="comparison-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-head text-lg font-semibold">Tax Liability &amp; ITC Comparison</div>
          <div className="small-label">GSTR-1 vs GSTR-3B liability · ITC GSTR-3B vs GSTR-2B</div>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="report-fy-select" value={fy} onChange={(e) => setFy(e.target.value)}
            className="rounded-md bg-secondary/50 border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500">
            {FY_OPTIONS.map((f) => <option key={f} value={f}>FY {f}</option>)}
          </select>
          <button data-testid="report-download-button" onClick={download} disabled={!report}
            className="flex items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-4 py-2 text-sm font-medium">
            <Download className="h-4 w-4" /> Download Excel
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {[["liability", "Tax Liability Summary"], ["comparison", "Comparison Summary"]].map(([k, label]) => (
          <button key={k} data-testid={`report-view-${k}`} onClick={() => setView(k)}
            className={`rounded-md px-3 py-1.5 text-xs font-mono border ${view === k ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" : "border-border text-muted-foreground"}`}>{label}</button>
        ))}
      </div>

      {busy && <div className="text-muted-foreground font-mono text-sm">Building report…</div>}

      {!busy && view === "liability" && tl && (
        <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid="report-liability-table">
          <table className="w-full text-xs font-mono">
            <thead className="text-muted-foreground border-b border-border">
              <tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:text-right [&>th:first-child]:text-left">
                <th rowSpan={2}>Period</th>
                <th colSpan={5} className="!text-center border-l border-border text-indigo-300">Liability as per GSTR-1</th>
                <th colSpan={5} className="!text-center border-l border-border text-sky-300">Paid as per GSTR-3B</th>
                <th colSpan={5} className="!text-center border-l border-border text-amber-300">Shortfall(-)/Excess(+)</th>
                <th rowSpan={2} className="border-l border-border">% Paid</th>
              </tr>
              <tr className="[&>th]:px-2 [&>th]:py-1 [&>th]:text-right text-[10px]">
                {[0, 1, 2].map((g) => (
                  <React.Fragment key={g}>
                    <th className="border-l border-border">IGST</th><th>CGST</th><th>SGST</th><th>CESS</th><th className="font-bold">TOTAL</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...tl.rows, tl.total].map((r, i) => (
                <tr key={r.label} data-testid={`report-liability-row-${i}`}
                  className={`border-b border-border/50 [&>td]:px-2 [&>td]:py-1.5 [&>td]:text-right [&>td:first-child]:text-left ${r.label === "Total" ? "bg-secondary/40 font-bold" : ""}`}>
                  <td>{r.label}</td>
                  {["l1", "p3", "sh"].map((seg) => (
                    <React.Fragment key={seg}>
                      <td className="border-l border-border/40">{num(r[seg].igst)}</td>
                      <td>{num(r[seg].cgst)}</td>
                      <td>{num(r[seg].sgst)}</td>
                      <td>{num(r[seg].cess)}</td>
                      <td className="font-semibold">{num(r[seg].total)}</td>
                    </React.Fragment>
                  ))}
                  <td className="border-l border-border/40">{r.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!busy && view === "comparison" && cmp && (
        <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid="report-comparison-table">
          <table className="w-full text-xs font-mono">
            <thead className="text-muted-foreground border-b border-border">
              <tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:text-right [&>th:first-child]:text-left">
                <th rowSpan={2}>Period</th>
                <th colSpan={5} className="!text-center border-l border-border text-indigo-300">Tax Liability [GSTR-3B vs GSTR-1]</th>
                <th colSpan={5} className="!text-center border-l border-border text-sky-300">ITC [GSTR-3B vs GSTR-2B]</th>
              </tr>
              <tr className="[&>th]:px-2 [&>th]:py-1 [&>th]:text-right text-[10px]">
                <th className="border-l border-border">GSTR-1</th><th>GSTR-3B</th><th>Short/Excess</th><th>Cumulative</th><th>Cum %</th>
                <th className="border-l border-border">GSTR-3B</th><th>GSTR-2B</th><th>Short/Excess</th><th>Cumulative</th><th>Cum %</th>
              </tr>
            </thead>
            <tbody>
              {[...cmp.rows, cmp.total].map((r, i) => (
                <tr key={r.label} data-testid={`report-comparison-row-${i}`}
                  className={`border-b border-border/50 [&>td]:px-2 [&>td]:py-1.5 [&>td]:text-right [&>td:first-child]:text-left ${r.label === "Total" ? "bg-secondary/40 font-bold" : ""}`}>
                  <td>{r.label}</td>
                  <td className="border-l border-border/40">{num(r.liab_g1)}</td>
                  <td>{num(r.liab_g3)}</td>
                  <td className={r.liab_short < 0 ? "text-rose-400" : ""}>{num(r.liab_short)}</td>
                  <td>{num(r.liab_cum)}</td>
                  <td>{r.liab_cum_pct}%</td>
                  <td className="border-l border-border/40">{num(r.itc_g3)}</td>
                  <td>{num(r.itc_2b)}</td>
                  <td className={r.itc_short < 0 ? "text-rose-400" : ""}>{num(r.itc_short)}</td>
                  <td>{num(r.itc_cum)}</td>
                  <td>{r.itc_cum_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------ Page ------------ */
export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "returns");
  const urlTab = searchParams.get("tab");
  useEffect(() => { if (urlTab) { setTab(urlTab); } }, [urlTab]);
  const [client, setClient] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [period, setPeriod] = useState(searchParams.get("period") || DEFAULT_PERIOD);

  useEffect(() => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id]);

  if (!client) return <div className="text-muted-foreground font-mono">Loading client…</div>;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-center gap-4">
        <div className="h-12 w-12 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
          <Building2 className="h-6 w-6 text-indigo-400" />
        </div>
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{client.legal_name}</h1>
          <div className="font-mono text-sm text-muted-foreground">{client.gstin} · State {client.home_state}</div>
        </div>
        <div className="w-full sm:w-auto sm:ml-auto">
          <label className="small-label block">Return Period</label>
          <select data-testid="period-select" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="mt-1 rounded-md bg-secondary/50 border border-border px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500">
            {(PERIOD_OPTIONS.includes(period) ? PERIOD_OPTIONS : [period, ...PERIOD_OPTIONS]).map((p) => <option key={p} value={p}>{periodLabel(p)} ({p})</option>)}
          </select>
        </div>
      </div>

      {user.role === "reader" && (
        <div data-testid="reader-readonly-banner" className="rounded-md border border-slate-500/30 bg-slate-500/10 px-4 py-2 text-xs font-mono text-slate-300">
          Read-only access · you can view returns, reconciliation and reports, and download Excel exports.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-card border border-border max-w-full h-auto flex-wrap justify-start">
          <TabsTrigger value="returns" data-testid="tab-returns">Returns</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Import Data</TabsTrigger>
          <TabsTrigger value="reconcile" data-testid="tab-reconcile">GSTR-2B Reconcile</TabsTrigger>
          <TabsTrigger value="annual" data-testid="tab-annual">Annual · 9 / 9C / ITC-04</TabsTrigger>
          <TabsTrigger value="ledgers" data-testid="tab-ledgers">Ledgers</TabsTrigger>
          <TabsTrigger value="eway" data-testid="tab-eway">E-way Bills</TabsTrigger>
          <TabsTrigger value="hsn" data-testid="tab-hsn">HSN Summary</TabsTrigger>
          <TabsTrigger value="itc" data-testid="tab-itc-checks">2B Pending &amp; Blocked ITC</TabsTrigger>
          <TabsTrigger value="availed" data-testid="tab-availed-itc">Availed ITC vs 3B</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report"><BarChart3 className="h-4 w-4 mr-1.5" /> Comparison Report</TabsTrigger>
          <TabsTrigger value="portal" data-testid="tab-portal"><PlugZap className="h-4 w-4 mr-1.5" /> GST Portal</TabsTrigger>
        </TabsList>

        <TabsContent value="returns" className="mt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <ReturnCard clientId={id} returnType="GSTR1" role={user.role} period={period} onChange={() => setRefreshKey((k) => k + 1)} key={`g1-${period}-${refreshKey}`} />
            <ReturnCard clientId={id} returnType="GSTR3B" role={user.role} period={period} onChange={() => setRefreshKey((k) => k + 1)} key={`g3-${period}-${refreshKey}`} />
          </div>
        </TabsContent>
        <TabsContent value="import" className="mt-4"><ImportPanel clientId={id} period={period} key={`imp-${period}`} readOnly={user.role === "reader"} /></TabsContent>
        <TabsContent value="reconcile" className="mt-4"><ReconcilePanel clientId={id} period={period} key={`rec-${period}`} readOnly={user.role === "reader"} /></TabsContent>
        <TabsContent value="annual" className="mt-4"><AnnualReturnsPanel clientId={id} role={user.role} /></TabsContent>
        <TabsContent value="ledgers" className="mt-4"><LedgersPanel clientId={id} /></TabsContent>
        <TabsContent value="eway" className="mt-4"><EwayPanel clientId={id} period={period} key={`ewb-${period}`} /></TabsContent>
        <TabsContent value="hsn" className="mt-4"><HsnReportPanel clientId={id} /></TabsContent>
        <TabsContent value="itc" className="mt-4"><ItcChecksPanel clientId={id} /></TabsContent>
        <TabsContent value="availed" className="mt-4"><AvailedItcPanel clientId={id} /></TabsContent>
        <TabsContent value="report" className="mt-4"><ComparisonReport clientId={id} /></TabsContent>
        <TabsContent value="portal" className="mt-4"><PortalPanel clientId={id} period={period} gstin={client.gstin} role={user.role} readOnly={user.role === "reader"} /></TabsContent>
      </Tabs>
    </div>
  );
}
